import React, { useState } from 'react';
import { Avatar, Dropdown, Button, Space, Typography } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Calendar,
  Wallet,
  Banknote,
  Users,
  BookOpen,
  BarChart3,
  FileText,
  Briefcase,
  Menu as MenuIcon,
  X,
  LogOut,
  User,
  Settings,
  Monitor,
  Store,
  ShoppingBag,
  Database,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';
import ThemeToggle from '../ThemeToggle';
import { usePrefetch } from '../../hooks/usePrefetch';
import type { MenuProps } from 'antd';

const { Text } = Typography;

interface MenuItem {
  key: string;
  icon: React.ComponentType<{className?: string}>;
  label: string;
}

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { setTheme } = useThemeStore();
  const [collapsed, setCollapsed] = useState(false);
  const { prefetchExpenses, prefetchLeaves, prefetchUsers, prefetchTasks, prefetchSales, prefetchBooks } = usePrefetch();

  // Initialize theme on mount
  React.useEffect(() => {
    const savedTheme = localStorage.getItem('bookrum-theme-storage');
    if (savedTheme) {
      const { state } = JSON.parse(savedTheme);
      setTheme(state.theme);
    }
  }, [setTheme]);

  // All menu items - always visible (permission checked on page access)
  const getMenuItems = (): MenuItem[] => {
    return [
      {
        key: '/dashboard',
        icon: Home,
        label: '대시보드',
      },
      {
        key: '/leaves',
        icon: Calendar,
        label: '휴가 관리',
      },
      {
        key: '/expenses',
        icon: Wallet,
        label: '지출품의',
      },
      {
        key: '/users',
        icon: Users,
        label: '사용자 관리',
      },
      {
        key: '/books',
        icon: BookOpen,
        label: '도서관리',
      },
      {
        key: '/sales',
        icon: BarChart3,
        label: '매출 관리',
      },
      {
        key: '/budgets',
        icon: Banknote,
        label: '예산 관리',
      },
      {
        key: '/partners',
        icon: Store,
        label: '거래처 관리',
      },
      {
        key: '/wiki',
        icon: FileText,
        label: '회사 위키',
      },
      {
        key: '/tasks',
        icon: Briefcase,
        label: '업무 관리',
      },
      {
        key: '/online-marketing',
        icon: Monitor,
        label: '온라인 마케팅 관리',
      },
      {
        key: '/offline-marketing',
        icon: ShoppingBag,
        label: '오프라인 영업 관리',
      },
      {
        key: '/data-manager',
        icon: Database,
        label: '데이터 관리자',
      },
    ];
  };

  const handleMenuClick = (key: string) => {
    navigate(key);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <User className="h-4 w-4" />,
      label: '프로필',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <Settings className="h-4 w-4" />,
      label: '설정',
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogOut className="h-4 w-4" />,
      label: '로그아웃',
      onClick: handleLogout,
    },
  ];

  const menuItems = getMenuItems();

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Mobile sidebar backdrop */}
      {collapsed && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setCollapsed(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 z-50 h-full w-64 border-r transition-all duration-200 ease-in-out
          lg:translate-x-0 ${collapsed ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ 
          backgroundColor: 'var(--bg-secondary)', 
          borderColor: 'var(--border)' 
        }}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-4 border-b" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <div className="text-2xl font-serif font-bold" style={{ color: 'var(--text-primary)' }}>BOOKRUM</div>
              <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>ERP</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCollapsed(false)}
                className="lg:hidden hover:opacity-80 transition-opacity"
                style={{ color: 'var(--text-secondary)' }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-3 py-4">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.key || 
                (item.key !== '/dashboard' && location.pathname.startsWith(item.key));
              
              return (
                <button
                  key={item.key}
                  onClick={() => handleMenuClick(item.key)}
                  className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition text-left border border-transparent"
                  style={isActive ? {
                    backgroundColor: 'rgba(125, 211, 192, 0.1)',
                    color: 'var(--primary)',
                    borderColor: 'rgba(125, 211, 192, 0.2)'
                  } : {
                    color: 'var(--text-secondary)'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'rgba(125, 211, 192, 0.05)';
                      e.currentTarget.style.color = 'var(--text-primary)';
                    }
                    // 페이지 데이터 프리페치
                    switch (item.key) {
                      case '/expenses':
                        prefetchExpenses();
                        break;
                      case '/leaves':
                        prefetchLeaves();
                        break;
                      case '/users':
                        prefetchUsers();
                        break;
                      case '/tasks':
                        prefetchTasks();
                        break;
                      case '/sales':
                        prefetchSales();
                        break;
                      case '/books':
                        prefetchBooks();
                        break;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--text-secondary)';
                    }
                  }}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="border-t p-4" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition hover:bg-red-50 hover:text-red-600"
              style={{ color: 'var(--text-secondary)' }}
            >
              <LogOut className="h-5 w-5" />
              <span>로그아웃</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header 
          className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b backdrop-blur px-4 lg:px-6 transition-colors duration-300"
          style={{ 
            borderColor: 'var(--border)',
            backgroundColor: 'var(--header-bg)'
          }}
        >
          <button
            onClick={() => setCollapsed(true)}
            className="lg:hidden hover:opacity-80 transition-opacity"
            style={{ color: 'var(--text-secondary)' }}
          >
            <MenuIcon className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-serif font-semibold" style={{ color: 'var(--text-primary)' }}>
                {user?.name} 님, 안녕하세요!
              </h1>
              <span className="text-sm px-2 py-1 rounded-md" style={{ 
                color: 'var(--primary)', 
                backgroundColor: 'rgba(125, 211, 192, 0.1)',
                border: '1px solid rgba(125, 211, 192, 0.2)'
              }}>
                {user?.department} · {user?.position}
              </span>
            </div>
          </div>

          <Space size="large">
            <ThemeToggle />

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Button type="text" className="flex items-center gap-2 text-left">
                <Avatar style={{ backgroundColor: 'var(--primary)' }}>
                  {user?.name?.[0]?.toUpperCase()}
                </Avatar>
                <Space direction="vertical" size={0} className="text-left">
                  <Text strong style={{ color: 'var(--text-primary)' }}>{user?.name}</Text>
                  <Text className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {user?.department} · {user?.position}
                  </Text>
                </Space>
              </Button>
            </Dropdown>
          </Space>
        </header>

        {/* Page content */}
        <main className="flex-1 flex flex-col min-h-0">
          <div key={location.pathname} className="flex-1 animate-fadeIn">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
